/**
 * 登录页 — 邮箱 + 密码
 * - 已绑 TOTP → 跳 /auth/verify-2fa 携临时 token
 * - 未绑 TOTP → 跳 /auth/bind-totp 携临时 token（codex P1 #2）
 * - 已是完整登录态 → 写 session + 跳 dashboard
 *
 * 皮肤 1:1 移植 design/Novel-admin/Login.html step-1（邮箱+密码+继续）；逻辑保持不变。
 */

import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AdminApiError } from '~/api/errors';
import { applyLoginDefaults } from '~/app/app-scope-store';
import { login } from '~/hooks/use-auth';
import { useAuthStore } from '~/stores/auth-store';
import { PasswordInput } from '~/ui/PasswordInput';
import { toast } from '~/ui/Toast';

export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromPath = params.get('from') ?? '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // 「在此设备保持登录 · 14 天」默认勾选（对齐设计稿 step-1）；经 router state 传到第二步生效
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const setSession = useAuthStore((s) => s.setSession);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !password) return;
    setAuthError(null);
    setSubmitting(true);
    try {
      const res = await login(email, password);
      if (res.stage === 'verify_2fa_required') {
        navigate(
          `/auth/verify-2fa?token=${encodeURIComponent(res.verify_2fa_token)}&from=${encodeURIComponent(fromPath)}&remember=${remember ? '1' : '0'}`,
          // 邮箱经 router state 传递（不放 URL，避免 PII 进 query string），供第二步用户卡片展示；
          // 「14 天保持登录」放 query（非 PII）→ 整页刷新仍保留、不回退 7 天
          { state: { email, remember } },
        );
      } else if (res.stage === 'bind_totp_required') {
        // codex P1 #2：未绑定 TOTP 必须先完成 bind-totp 才能拿正式 access
        navigate(
          `/auth/bind-totp?token=${encodeURIComponent(res.bind_totp_token)}&from=${encodeURIComponent(fromPath)}&remember=${remember ? '1' : '0'}`,
          // 邮箱经 router state 传递，供绑定页用户卡片展示（对齐设计稿 step-2-setup）；
          // 「14 天保持登录」放 query（非 PII）→ 整页刷新仍保留、不回退 7 天
          { state: { email, remember } },
        );
      } else {
        setSession({ access: res.access, refresh: res.refresh, user: res.user });
        // 套用本机默认 App scope；from 无深链时跳默认落地页
        const defaultPage = applyLoginDefaults();
        navigate(fromPath !== '/' ? fromPath : defaultPage, { replace: true });
      }
    } catch (err) {
      const message = err instanceof AdminApiError ? err.message : '登录失败，请重试';
      setAuthError(message);
      toast(message, { tone: 'neg' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="step-indicator">
        <span>步骤 1 / 2</span>
        <span className="step-dot active" />
        <span className="step-dot" />
      </div>

      <div className="step">
        <div style={{ marginBottom: 24 }}>
          <h2 className="form-title">登录</h2>
          <p className="form-sub">请使用运营账户登录，未授权访问将被记录</p>
        </div>

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="field">
            <label className="field-label" htmlFor="login-email">
              邮箱
            </label>
            <input
              id="login-email"
              className="input-big"
              type="email"
              placeholder="you@rivmoon.com"
              autoComplete="username"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setAuthError(null);
              }}
              required
            />
          </div>
          <div className="field">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label className="field-label" htmlFor="login-pw" style={{ flex: 1 }}>
                密码
              </label>
              <Link
                to="/auth/password-reset/request"
                style={{ fontSize: 11.5, color: 'var(--accent)', textDecoration: 'none' }}
              >
                忘记密码？
              </Link>
            </div>
            <PasswordInput
              id="login-pw"
              className="input-big"
              placeholder="••••••••"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setAuthError(null);
              }}
              required
            />
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12.5,
              color: 'var(--fg-muted)',
              cursor: 'pointer',
              marginTop: 4,
            }}
          >
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              style={{ accentColor: 'var(--accent)', width: 15, height: 15 }}
            />
            在此设备保持登录 · 14 天
          </label>

          {authError && (
            <div className="err">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4" />
                <path d="M12 16h.01" />
              </svg>
              <span>{authError}</span>
            </div>
          )}

          <button type="submit" className="btn-big" style={{ marginTop: 4 }} disabled={submitting}>
            {submitting ? '登录中…' : '继续'}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </button>
        </form>
      </div>
    </>
  );
}
